/**
 * Canonical status/enum values shared across frontend, backend, and infra.
 * Pattern: a frozen const object + a derived union type with the same name.
 * Stored values are snake_case strings; never persist display labels.
 */

export const UserRole = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  FINANCE: 'finance',
  WAREHOUSE: 'warehouse',
  SALES_MANAGER: 'sales_manager',
  SALES_REP: 'sales_rep',
  CUSTOMER_USER: 'customer_user',
  DISTRIBUTOR_ADMIN: 'distributor_admin',
  DISTRIBUTOR_USER: 'distributor_user',
  DRIVER: 'driver',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const AccountStatus = {
  PENDING: 'pending',
  ACTIVE: 'active',
  ON_HOLD: 'on_hold',
  SUSPENDED: 'suspended',
  REJECTED: 'rejected',
  CLOSED: 'closed',
} as const;
export type AccountStatus = (typeof AccountStatus)[keyof typeof AccountStatus];

/** What an admin can do to a customer or distributor account (POST .../status). */
export const AccountAction = {
  APPROVE: 'approve',
  REJECT: 'reject',
  HOLD: 'hold',
  SUSPEND: 'suspend',
  REACTIVATE: 'reactivate',
  REOPEN: 'reopen',
} as const;
export type AccountAction = (typeof AccountAction)[keyof typeof AccountAction];

/**
 * Valid account transitions: current status -> action -> new status. The API and the web
 * buttons both read this table, so a button never offers something the server would refuse.
 * `closed` is final (nothing leaves it).
 */
export const ACCOUNT_TRANSITIONS: Record<AccountStatus, Partial<Record<AccountAction, AccountStatus>>> = {
  pending: { approve: 'active', reject: 'rejected' },
  active: { hold: 'on_hold', suspend: 'suspended' },
  on_hold: { reactivate: 'active', suspend: 'suspended' },
  suspended: { reactivate: 'active' },
  rejected: { reopen: 'pending' },
  closed: {},
};

/** Actions that must say why (kept in the audit log and shown on the account). */
export const ACCOUNT_ACTIONS_REQUIRING_REASON: readonly AccountAction[] = [
  AccountAction.REJECT,
  AccountAction.HOLD,
  AccountAction.SUSPEND,
];

/** Statuses from which an account may not create or submit orders. */
export const ACCOUNT_STATUSES_BLOCKING_ORDERS: readonly AccountStatus[] = [
  AccountStatus.SUSPENDED,
  AccountStatus.REJECTED,
  AccountStatus.CLOSED,
];

export function nextAccountStatus(from: AccountStatus, action: AccountAction): AccountStatus | undefined {
  return ACCOUNT_TRANSITIONS[from][action];
}

/** The actions available from a status, in the order the buttons are shown. */
export function accountActionsFor(status: AccountStatus): AccountAction[] {
  return Object.keys(ACCOUNT_TRANSITIONS[status]) as AccountAction[];
}

export const PaymentTerms = {
  UPFRONT: 'upfront',
  NET_7: 'net_7',
  NET_15: 'net_15',
  NET_30: 'net_30',
  CUSTOM: 'custom',
} as const;
export type PaymentTerms = (typeof PaymentTerms)[keyof typeof PaymentTerms];

export const OrderStatus = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  PENDING_APPROVAL: 'pending_approval',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  PICKING: 'picking',
  DISPATCHED: 'dispatched',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
  CLOSED: 'closed',
} as const;
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/**
 * Statuses an order can still be cancelled from: before fulfilment starts. The API enforces
 * it and the web only offers the cancel button in these, so both read this one list.
 */
export const CANCELLABLE_ORDER_STATUSES: readonly OrderStatus[] = [
  OrderStatus.DRAFT,
  OrderStatus.SUBMITTED,
  OrderStatus.PENDING_APPROVAL,
  OrderStatus.APPROVED,
];

export const QuoteStatus = {
  DRAFT: 'draft',
  SENT: 'sent',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  CONVERTED: 'converted',
} as const;
export type QuoteStatus = (typeof QuoteStatus)[keyof typeof QuoteStatus];

export const PaymentStatus = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  REFUNDED: 'refunded',
  CANCELLED: 'cancelled',
} as const;
export type PaymentStatus = (typeof PaymentStatus)[keyof typeof PaymentStatus];

export const PaymentMethod = {
  PAGO_MOVIL: 'pago_movil',
  ZELLE: 'zelle',
  TRANSFERENCIA: 'transferencia',
  EFECTIVO: 'efectivo',
  PUNTO_DE_VENTA: 'punto_de_venta',
  NOTA_DE_CREDITO: 'nota_de_credito',
  AJUSTE_MANUAL: 'ajuste_manual',
  STRIPE: 'stripe',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];

export const InvoiceStatus = {
  DRAFT: 'draft',
  ISSUED: 'issued',
  PARTIALLY_PAID: 'partially_paid',
  PAID: 'paid',
  OVERDUE: 'overdue',
  VOID: 'void',
} as const;
export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

/** Customer-facing availability labels. Exact stock is never exposed to customers. */
export const AvailabilityStatus = {
  DISPONIBLE_HOY: 'disponible_hoy',
  DISPONIBLE_24_72H: 'disponible_24_72h',
  BAJO_PEDIDO: 'bajo_pedido',
  RESERVADO: 'reservado',
  AGOTADO: 'agotado',
  CONSULTAR: 'consultar',
} as const;
export type AvailabilityStatus = (typeof AvailabilityStatus)[keyof typeof AvailabilityStatus];

/** Spanish display labels for availability (UI only — never persisted). */
export const AVAILABILITY_LABELS_ES: Record<AvailabilityStatus, string> = {
  disponible_hoy: 'Disponible hoy',
  disponible_24_72h: 'Disponible en 24–72 horas',
  bajo_pedido: 'Bajo pedido',
  reservado: 'Reservado',
  agotado: 'Agotado',
  consultar: 'Consultar disponibilidad',
};

export const CurrencyCode = {
  USD: 'USD',
  VES: 'VES',
} as const;
export type CurrencyCode = (typeof CurrencyCode)[keyof typeof CurrencyCode];

export const MovementType = {
  RECEIVE: 'receive',
  RESERVE: 'reserve',
  RELEASE_RESERVATION: 'release_reservation',
  PICK: 'pick',
  SHIP: 'ship',
  TRANSFER_OUT: 'transfer_out',
  TRANSFER_IN: 'transfer_in',
  ADJUSTMENT: 'adjustment',
  RETURN: 'return',
} as const;
export type MovementType = (typeof MovementType)[keyof typeof MovementType];

export const DocumentStatus = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;
export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export const DocumentType = {
  RIF: 'rif',
  LEGAL: 'legal',
  AGREEMENT: 'agreement',
  PAYMENT_PROOF: 'payment_proof',
  INVOICE_PDF: 'invoice_pdf',
  PRODUCT_IMAGE: 'product_image',
  OTHER: 'other',
} as const;
export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

/** How a promotion discounts: a % off, a fixed USD amount off, or a volume rebate %. */
export const PromotionKind = {
  PERCENTAGE: 'percentage',
  FIXED: 'fixed',
  VOLUME_REBATE: 'volume_rebate',
} as const;
export type PromotionKind = (typeof PromotionKind)[keyof typeof PromotionKind];

export const NotificationType = {
  ORDER_CREATED: 'order_created',
  ORDER_APPROVED: 'order_approved',
  ORDER_REJECTED: 'order_rejected',
  PAYMENT_UPLOADED: 'payment_uploaded',
  PAYMENT_APPROVED: 'payment_approved',
  PAYMENT_REJECTED: 'payment_rejected',
  ORDER_DISPATCHED: 'order_dispatched',
  ORDER_DELIVERED: 'order_delivered',
  INVOICE_DUE: 'invoice_due',
  INVOICE_OVERDUE: 'invoice_overdue',
  PROMO_AVAILABLE: 'promo_available',
  REBATE_EARNED: 'rebate_earned',
  STOCK_AVAILABLE: 'stock_available',
  ACCOUNT_APPROVED: 'account_approved',
  CREDIT_HOLD: 'credit_hold',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

export const NotificationChannel = {
  IN_APP: 'in_app',
  EMAIL: 'email',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

export const NotificationStatus = {
  QUEUED: 'queued',
  SENT: 'sent',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  READ: 'read',
} as const;
export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];

/** DynamoDB single-table entity discriminator (`entityType` attribute). */
export const EntityType = {
  USER: 'USER',
  CUSTOMER: 'CUSTOMER',
  DISTRIBUTOR: 'DISTRIBUTOR',
  SALES_REP: 'SALES_REP',
  BRAND: 'BRAND',
  CATEGORY: 'CATEGORY',
  PRODUCT: 'PRODUCT',
  SKU: 'SKU',
  PRICE_LIST: 'PRICE_LIST',
  INVENTORY_BALANCE: 'INVENTORY_BALANCE',
  INVENTORY_MOVEMENT: 'INVENTORY_MOVEMENT',
  ORDER: 'ORDER',
  ORDER_ITEM: 'ORDER_ITEM',
  RESERVATION: 'RESERVATION',
  QUOTE: 'QUOTE',
  INVOICE: 'INVOICE',
  PAYMENT: 'PAYMENT',
  DOCUMENT: 'DOCUMENT',
  NOTIFICATION: 'NOTIFICATION',
  AUDIT_LOG: 'AUDIT_LOG',
  SETTING: 'SETTING',
  PROMOTION: 'PROMOTION',
} as const;
export type EntityType = (typeof EntityType)[keyof typeof EntityType];
